import { FC, useState, useRef, useEffect } from 'react';

import { PulseButton } from './ui/PulseButton';
import { Icon } from './icons/Icon';
import { COPY } from '../lib/copyDictionary';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import { useToast } from '../hooks/use-toast';

interface VoiceRecorderProps {
    onRecordingComplete: (audioBlob: Blob, duration: number) => void;
}

export const VoiceRecorder: FC<VoiceRecorderProps> = ({ onRecordingComplete }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationRef = useRef<number>(0);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    // [A3] Use ref to track recording time to avoid stale closure in onstop
    const recordingTimeRef = useRef<number>(0);
    const { playSound } = useGamenics();
    const { toast } = useToast();

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Setup Visualizer
            const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
            const analyser = audioCtx.createAnalyser();
            const source = audioCtx.createMediaStreamSource(stream);
            source.connect(analyser);
            analyser.fftSize = 256;

            audioContextRef.current = audioCtx;
            analyserRef.current = analyser;
            sourceRef.current = source;

            mediaRecorderRef.current = new MediaRecorder(stream);
            chunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' }); // Chrome/Firefox default
                const url = URL.createObjectURL(blob);
                setAudioUrl(url);
                // [A3] Use ref value to avoid stale closure
                onRecordingComplete(blob, recordingTimeRef.current); // Report duration

                // Cleanup Visualizer
                if (animationRef.current) cancelAnimationFrame(animationRef.current);
                if (audioContextRef.current) audioContextRef.current.close();
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            setRecordingTime(0);

            // Timer
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => {
                    const newTime = prev + 0.1;
                    recordingTimeRef.current = newTime; // [A3] Keep ref in sync
                    return newTime;
                });
            }, 100);

            // Visualize
            drawVisualizer();

        } catch (err) {
            console.error('Error accessing microphone:', err);
            toast({
                title: 'マイクへのアクセスエラー',
                description: 'マイクへのアクセスが拒否されました。設定を確認してください。',
                variant: 'destructive',
            });
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (timerRef.current) clearInterval(timerRef.current);
        }
    };

    const retake = () => {
        setAudioUrl(null);
        setRecordingTime(0);
    };

    const drawVisualizer = () => {
        if (!canvasRef.current || !analyserRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const draw = () => {
            if (!isRecording) return;
            animationRef.current = requestAnimationFrame(draw);
            analyserRef.current!.getByteFrequencyData(dataArray);

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const barWidth = (canvas.width / bufferLength) * 2.5;
            let barHeight;
            let x = 0;

            for (let i = 0; i < bufferLength; i++) {
                barHeight = dataArray[i] / 2;
                ctx.fillStyle = `rgb(${barHeight + 100}, 50, 255)`; // Neon-ish color
                ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
                x += barWidth + 1;
            }
        };

        draw();
    };

    useEffect(() => {
        // Cleanup on unmount
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close();
            }
        };
    }, []);

    return (
        <div className="flex flex-col items-center gap-6 w-full">
            {/* Visualizer Area */}
            <div className="w-full h-32 bg-cream-2 rounded-pop-md overflow-hidden flex items-center justify-center relative border-2 border-ink">
                {isRecording ? (
                    <canvas ref={canvasRef} width={300} height={128} className="w-full h-full" />
                ) : audioUrl ? (
                    <div className="flex flex-col items-center">
                        <audio src={audioUrl} controls className="mb-2" />
                        <p className="text-role-voice text-sm">録音完了 ({recordingTime.toFixed(1)}s)</p>
                    </div>
                ) : (
                    <p className="text-ink-3 text-sm">マイクボタンを押して録音開始</p>
                )}

                {/* Timer Overlay */}
                {isRecording && (
                    <div className="absolute top-2 right-4 text-pop font-mono text-xl animate-pulse">
                        {recordingTime.toFixed(1)}s
                    </div>
                )}
            </div>

            {/* Controls */}
            <div className="flex gap-4">
                {!audioUrl ? (
                    !isRecording ? (
                        <PulseButton
                            onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); startRecording(); }}
                            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 border-2 border-ink shadow-hard-sm p-0 text-white"
                        >
                            <Icon name="mic" size={26} className="text-white" />
                        </PulseButton>
                    ) : (
                        <PulseButton
                            isPrimary
                            onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); stopRecording(); }}
                            className="w-16 h-16 rounded-full bg-ink hover:bg-ink/90 border-2 border-ink shadow-hard-sm p-0"
                        >
                            <span className="w-5 h-5 bg-sun rounded-sm" />
                        </PulseButton>
                    )
                ) : (
                    <PulseButton
                        variant="outline"
                        onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); retake(); }}
                    >
                        {COPY.retry}
                    </PulseButton>
                )}
            </div>
        </div>
    );
};
